import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { apiGet, apiPut } from '../../lib/api';
import { toast } from 'sonner';
import { Save, ArrowLeft, Bell, Loader2, Mail, MessageCircle, Monitor } from 'lucide-react';

const CHANNELS = [
  { key: 'email', label: 'Email', icon: Mail },
  { key: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, note: 'Requires WhatsApp Business API setup' },
  { key: 'in_app', label: 'In-App Bell', icon: Monitor },
];

export default function Notifications() {
  const navigate = useNavigate();
  const [data, setData] = useState({ channels: {}, events: {} });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiGet('/settings/notifications').then((d) => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const toggleChannel = (k) => setData((d) => ({ ...d, channels: { ...d.channels, [k]: !d.channels[k] } }));

  const toggleEvent = (eventKey, channelKey) => {
    setData((d) => ({
      ...d,
      events: { ...d.events, [eventKey]: { ...d.events[eventKey], [channelKey]: !d.events[eventKey][channelKey] } },
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await apiPut('/settings/notifications', data);
      toast.success('Notification settings saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  if (loading) return <div className="p-8 flex items-center gap-2 text-slate-500"><Loader2 size={14} className="animate-spin" /> Loading…</div>;

  return (
    <div className="p-6 md:p-8" data-testid="notifications-page">
      <button onClick={() => navigate('/app/settings')} className="text-xs font-display font-bold uppercase tracking-wider text-slate-500 hover:text-[#1F7A6E] flex items-center gap-1 mb-3"><ArrowLeft size={12} /> Back to Settings</button>
      <PageHeader overline="Settings · Notifications" title="Notifications" subtitle="Choose how and when the platform alerts your team." icon={Bell} />

      {/* Master channels */}
      <div className="bg-white border border-slate-200 rounded-sm p-5 mt-6">
        <div className="text-[10px] font-display font-bold uppercase tracking-[0.16em] text-[#1F7A6E] mb-3">Master Channels</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {CHANNELS.map((c) => {
            const Icon = c.icon;
            const on = !!data.channels[c.key];
            return (
              <button
                key={c.key}
                onClick={() => toggleChannel(c.key)}
                data-testid={`channel-${c.key}`}
                className={`text-left p-3 border rounded-sm transition-colors ${on ? 'border-[#1F7A6E] bg-teal-50/50' : 'border-slate-200 hover:border-slate-400'}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${on ? 'bg-[#1F7A6E] text-white' : 'bg-slate-100 text-slate-400'}`}><Icon size={14} /></div>
                  <span className={`text-[10px] font-display font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm ${on ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{on ? 'ON' : 'OFF'}</span>
                </div>
                <div className="font-display font-bold text-sm mt-1">{c.label}</div>
                {c.note && <div className="text-[10px] text-slate-500 mt-0.5">{c.note}</div>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Per-event */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden mt-5">
        <div className="px-5 py-3 border-b border-slate-200 text-[10px] font-display font-bold uppercase tracking-[0.16em] text-[#1F7A6E]">
          Event-Wise Triggers
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-display">
            <tr>
              <th className="text-left py-2.5 px-4">Event</th>
              {CHANNELS.map((c) => <th key={c.key} className="text-center py-2.5 px-3 w-24">{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {Object.entries(data.events || {}).map(([eventKey, event]) => (
              <tr key={eventKey} className="border-t border-slate-100" data-testid={`event-${eventKey}`}>
                <td className="py-2.5 px-4">
                  <div className="font-semibold">{event.label}</div>
                  <div className="text-[10px] text-slate-400 font-mono">{eventKey}</div>
                </td>
                {CHANNELS.map((c) => {
                  const masterOn = !!data.channels[c.key];
                  const eventOn = !!event[c.key];
                  return (
                    <td key={c.key} className="text-center py-2.5 px-3">
                      <input
                        type="checkbox"
                        checked={eventOn && masterOn}
                        disabled={!masterOn}
                        onChange={() => toggleEvent(eventKey, c.key)}
                        className="w-4 h-4 accent-[#1F7A6E] cursor-pointer disabled:cursor-not-allowed disabled:opacity-30"
                        data-testid={`evt-${eventKey}-${c.key}`}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-6 flex justify-end sticky bottom-4">
        <button data-testid="notif-save-btn" onClick={save} disabled={saving} className="flex items-center gap-2 px-4 py-2.5 bg-[#1F7A6E] text-white text-sm rounded-sm font-display font-semibold hover:bg-[#14504E] disabled:opacity-50 shadow-lg">
          <Save size={14} /> {saving ? 'Saving…' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
}
